import { useCallback, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { logEvent } from 'firebase/analytics';

import {
  useReadNotificationByIdMutation,
  useRegisterPushTokenMutation,
  useRemovePushTokenMutation,
} from '@/api/queries/notifications';
import { analytics } from '@/firebase';
import { useAppSelector } from '@/redux/store';

// The APNs token of this device, kept to unregister it on logout
const PUSH_TOKEN_KEY = 'pushToken';

/**
 * Native app: asks for the notification permission once the player is logged
 * in and sends the device token to the backend (it re-sends it on every
 * launch, APNs / FCM may rotate it). Pushes only come from the server;
 * which ones the player gets is set on the Push notifications settings page.
 */
const usePushNotifications = (): void => {
  const token = useAppSelector((state) => state.user.token);
  const [registerPushToken] = useRegisterPushTokenMutation();
  const [readNotification] = useReadNotificationByIdMutation();

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !token) return;

    const registration = PushNotifications.addListener(
      'registration',
      ({ value }) => {
        try {
          localStorage.setItem(PUSH_TOKEN_KEY, value);
        } catch {
          // storage unavailable: logout just won't unregister the token
        }
        registerPushToken({ token: value, platform: Capacitor.getPlatform() });
      },
    );

    // tapped push (also the one that cold-started the app: the plugin keeps
    // it until a listener comes). pushId is the notification id: opening the
    // push reads it, so read_at doubles as push open stats
    const opened = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      ({ notification }) => {
        const { pushId, category } = notification.data ?? {};

        if (pushId) readNotification({ id: String(pushId) });

        if (analytics) logEvent(analytics, 'push_open', { category });
      },
    );

    const register = async (): Promise<void> => {
      let permission = await PushNotifications.checkPermissions();

      if (permission.receive === 'prompt') {
        permission = await PushNotifications.requestPermissions();
      }

      if (permission.receive === 'granted') {
        await PushNotifications.register();
      }
    };

    register().catch((error) => console.warn('push register failed', error));

    return (): void => {
      registration.then((listener) => listener.remove());
      opened.then((listener) => listener.remove());
    };
  }, [token]);
};

/** Stops this device from getting the current account's pushes. */
const useUnregisterPush = (): (() => Promise<void>) => {
  const [removePushToken] = useRemovePushTokenMutation();

  return useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return;

    const pushToken = localStorage.getItem(PUSH_TOKEN_KEY);

    if (!pushToken) return;

    localStorage.removeItem(PUSH_TOKEN_KEY);
    await removePushToken({ token: pushToken })
      .unwrap()
      .catch(() => undefined);
  }, [removePushToken]);
};

export { usePushNotifications, useUnregisterPush };
